// The full-screen photo preview's decisions (#393): where the picture comes
// from, what to say when only the small copy can be shown, and what a swipe
// means. Kept free of the browser so it can be tested on its own.

/**
 * Where the full size comes from: the device while the photo waits to upload
 * (its JPEG is still there), SharePoint once uploaded and there's signal, or
 * nowhere — offline, an uploaded photo shows only the thumbnail.
 */
export type PreviewSource = 'device' | 'sharepoint' | 'thumbnail';

export function previewSource(device: { image: Blob | null } | null, online: boolean): PreviewSource {
  if (device?.image) return 'device';
  return online ? 'sharepoint' : 'thumbnail';
}

/** The full size as the preview has it: loading, ready, or not to be had (offline, or the load failed). */
export type FullSize = { status: 'loading' } | { status: 'ready'; url: string } | { status: 'unavailable'; reason: 'offline' | 'failed' };

/** The line under the picture when the full size isn't shown; null when there's nothing to say. */
export function previewNote(full: FullSize, hasThumbnail: boolean): string | null {
  if (full.status !== 'unavailable') return null;
  if (full.reason === 'offline') {
    return hasThumbnail ? 'Showing the small copy — the full size needs signal.' : 'This photo is on SharePoint — it needs signal to show.';
  }
  return hasThumbnail ? 'Couldn’t load the full size from SharePoint — showing the small copy.' : 'Couldn’t load this photo from SharePoint. Try again with signal.';
}

/** How far a finger must travel (px) before a drag counts as a swipe. */
export const SWIPE_PX = 60;

/** What a drag of (dx, dy) px means: swipe down closes, left and right move along the strip. */
export function swipeAction(dx: number, dy: number): 'close' | 'next' | 'previous' | null {
  if (Math.abs(dy) > Math.abs(dx)) return dy > SWIPE_PX ? 'close' : null;
  if (dx < -SWIPE_PX) return 'next';
  if (dx > SWIPE_PX) return 'previous';
  return null;
}

/** The photo `step` along from `index`, held within the strip's `count` photos. */
export function stepIndex(index: number, step: number, count: number): number {
  return Math.min(count - 1, Math.max(0, index + step));
}
